import React, {
  useRef,
  useImperativeHandle,
  forwardRef,
} from "react";
import { useTranslation } from "react-i18next";
import { AudioPlayState } from "@/manager/manager/audio-controller-manager";
import {
  PlayIcon,
  PlayLoadingIcon,
  PlayingIcon,
  FavoriteIcon,
  FavoriteHoverIcon,
  FavoriteActiveIcon,
  CheckCircleActiveIcon,
  CheckCircleIcon,
  CheckCircleHoverIcon,
} from "@/components/ui/icon";
import { TruncatedText } from "@/components/common/truncated-text";
import { cn } from "@/utils/cn";

// 表格行骨架屏组件
const TableRowSkeleton: React.FC = () => {
  return (
    <tr className="border-b">
      <td className="px-6 py-5">
        <div className="w-20 h-4 bg-gray-200 rounded animate-pulse"></div>
      </td>
      <td className="px-6 py-5">
        <div className="w-12 h-4 bg-gray-200 rounded animate-pulse"></div>
      </td>
      <td className="px-6 py-5">
        <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
      </td>
      <td className="px-6 py-5">
        <div className="w-14 h-4 bg-gray-200 rounded animate-pulse"></div>
      </td>
      <td className="p-4">
        <div className="flex items-center justify-center gap-2">
          <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
          <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
          <div className="w-5 h-5 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </td>
    </tr>
  );
};

// 表格骨架屏组件
const TableSkeleton: React.FC = () => {
  // 生成足够的骨架屏行数以填满视窗
  const skeletonRows = Array.from({ length: 12 }, (_, index) => (
    <TableRowSkeleton key={index} />
  ));

  return <tbody>{skeletonRows}</tbody>;
};

// 添加ref接口
export interface AudioDataListRef {
  scrollToCurrentAudio: () => void;
}

interface AudioDataListProps {
  audios: Audio[];
  selectAudio?: Audio;
  playingAudio?: Audio;
  currentTime: number;
  duration: number;
  playState: AudioPlayState;
  loading: boolean;
  onVoiceSelect: (voice: Audio) => void;
  onPlayPause: (voice: Audio) => void;
  onToggleFavorite: (voice: Audio) => void;
  ref?: React.RefObject<HTMLVideoElement>;
}

/** 音频数据列表 */
export const AudioDataList = forwardRef<AudioDataListRef, AudioDataListProps>((props, ref) => {
  const {
    audios,
    selectAudio,
    playingAudio,
    currentTime,
    duration,
    playState,
    loading,
    onVoiceSelect,
    onPlayPause,
    onToggleFavorite,
  } = props;

  const { t } = useTranslation();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // 暴露滚动到当前音频的方法
  useImperativeHandle(ref, () => ({
    scrollToCurrentAudio: () => {
      if (!tableContainerRef.current) {
        return;
      }
      const selectors = `tr[data-audio-id="${selectAudio?.id}"]`;
      const audioRow = tableContainerRef.current.querySelector(selectors);

      if (audioRow) {
        audioRow.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // 添加高亮效果
        audioRow.classList.add("bg-i-active");
        setTimeout(() => {
          audioRow.classList.remove("bg-i-active");
        }, 2000);
      }
    },
  }));

  return (
    <div
      ref={tableContainerRef}
      className="flex-1 overflow-y-auto rounded-t-lg border"
    >
      <table className="w-full rounded-t-lg">
        <thead className="bg-gray-50 sticky top-0 border-b border-gray-200 h-11">
          <tr className="h-10">
            <th className="px-6 text-left text-xs font-medium text-[rgb(71,84,103)]">
              {t("modal_audio.audio_table.name")}
            </th>
            <th className="px-6 text-left text-xs font-medium text-[rgb(71,84,103)]">
              {t("modal_audio.audio_table.gender")}
            </th>
            <th className="px-6 text-left text-xs font-medium text-[rgb(71,84,103)]">
              {t("modal_audio.audio_table.age")}
            </th>
            <th className="px-6 text-left text-xs font-medium text-[rgb(71,84,103)]">
              {t("modal_audio.audio_table.language")}
            </th>
            {/* <th className="px-6 text-left text-xs font-medium text-[rgb(71,84,103)]">
                Emotion
              </th> */}
            <th className="px-6 text-center text-xs font-medium text-[rgb(71,84,103)]"></th>
          </tr>
        </thead>

        {/* 根据loading状态显示骨架屏或实际内容 */}
        {loading ? (
          <TableSkeleton />
        ) : (
          <tbody className="overflow-y-auto">
            {audios.map((audio, index) => {
              const isCurrent = selectAudio?.id === audio.id;
              const isPlaying =
                playingAudio?.id === audio.id &&
                playState === AudioPlayState.PLAYING;
              const isLoading =
                playingAudio?.id === audio.id &&
                playState === AudioPlayState.LOADING;
              return (
                <tr
                  key={`${audio.id}_${index}`}
                  data-audio-id={audio.id}
                  className={cn(
                    "border-b hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                  )}
                  onClick={() => onVoiceSelect(audio)}
                >
                  <td className="px-6 py-5 text-sm font-medium capitalize text-i-normal">
                    <TruncatedText className="w-24" tooltipClassName="capitalize" text={audio.name} />
                  </td>
                  <td className="px-6 py-5 text-sm capitalize text-i-normal">
                    {t(audio.gender)}
                  </td>
                  <td className="px-6 py-5 text-sm capitalize text-i-normal">
                    {t(audio.ageGroup)}
                  </td>
                  <td className="px-6 py-5 text-sm capitalize text-i-normal">
                    {t(audio.language)}
                  </td>
                  {/* <td className="px-6 py-5 text-sm capitalize text-i-normal">
                      {t(audio.emotion)}
                    </td> */}
                  <td className="p-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onPlayPause(audio);
                        }}
                        className="p-1 hover:bg-gray-200 rounded transition-colors"
                      >
                        {isLoading ? (
                          <PlayLoadingIcon className="w-5 h-5" />
                        ) : isPlaying ? (
                          <PlayingIcon
                            className="w-5 h-5"
                            progress={Math.ceil(
                              (currentTime / duration) * 100
                            )}
                          />
                        ) : (
                          <PlayIcon className="w-5 h-5" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleFavorite(audio);
                        }}
                        className={cn(
                          "group p-1 rounded transition-colors hover:bg-gray-200 cursor-pointer"
                        )}
                      >
                        {audio.isStarred ? (
                          <FavoriteActiveIcon className="w-5 h-5" />
                        ) : (
                          <>
                            <FavoriteIcon className="group-hover:hidden w-5 h-5" />
                            <FavoriteHoverIcon className="hidden group-hover:block w-5 h-5" />
                          </>
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onVoiceSelect(audio);
                        }}
                        className={cn(
                          "group p-1 rounded transition-colors hover:bg-gray-200 cursor-pointer"
                        )}
                      >
                        {isCurrent ? (
                          <CheckCircleActiveIcon className="w-5 h-5" />
                        ) : (
                          <>
                            <CheckCircleIcon className="group-hover:hidden w-5 h-5" />
                            <CheckCircleHoverIcon className="hidden group-hover:block w-5 h-5" />
                          </>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        )}
      </table>
    </div>
  )
})
